import { style, keyframes, globalStyle, styleVariants } from '@vanilla-extract/css';
import { vars } from '../../theme/contract.css';
import { reducedMotion } from '../../theme/media';

const toastIn = keyframes({
  '0%': { opacity: 0, transform: 'scale(0.95) translateY(-8px)', filter: 'blur(4px)' },
  '100%': { opacity: 1, transform: 'scale(1) translateY(0)', filter: 'blur(0)' },
});

const toastOut = keyframes({
  '0%': { opacity: 1, transform: 'scale(1)', filter: 'blur(0)' },
  '100%': { opacity: 0, transform: 'scale(0.95)', filter: 'blur(4px)' },
});

const toastGlowSweep = keyframes({
  '0%': { backgroundPosition: '-100% 0', opacity: '0.3' },
  '50%': { opacity: '1' },
  '100%': { backgroundPosition: '200% 0', opacity: '1' },
});

export const container = style({
  position: 'fixed',
  top: 16,
  right: 16,
  zIndex: vars.elevation.toast,
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.sm,
  pointerEvents: 'none',
});

export const toast = style({
  pointerEvents: 'auto',
  minWidth: 280,
  maxWidth: 380,
  padding: `${vars.spacing.padding.sm} ${vars.spacing.padding.md}`,
  borderRadius: vars.spacing.borderRadius.md,
  backgroundColor: vars.surface.liquidGlass.innerSurface.backgroundDense,
  backdropFilter: vars.surface.liquidGlass.backdropFilter.medium,
  WebkitBackdropFilter: vars.surface.liquidGlass.backdropFilter.medium,
  border: `1px solid ${vars.surface.liquidGlass.innerSurface.border}`,
  boxShadow: `${vars.shadow.toast}, ${vars.bright.borderGlow.default}`,
  animation: `${toastIn} ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}`,
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 10,
  position: 'relative',
  overflow: 'hidden',
  '@media': {
    [reducedMotion]: {
      animation: 'none',
    },
  },
});

// Top glow line sweep on entrance
globalStyle(`${toast}::after`, {
  content: '""',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '1px',
  background: `linear-gradient(90deg, transparent, ${vars.bright.primary.default}, transparent)`,
  backgroundSize: '50% 100%',
  boxShadow: `0 0 6px ${vars.bright.primary.glow}`,
  animation: `${toastGlowSweep} ${vars.animation.duration.slow} ${vars.animation.easing.easeOut} forwards`,
  pointerEvents: 'none',
  '@media': {
    [reducedMotion]: { animation: 'none' },
  },
});

export const toastExiting = style({
  animation: `${toastOut} ${vars.animation.duration.medium} ${vars.animation.easing.easeIn} forwards`,
  '@media': {
    [reducedMotion]: {
      animation: 'none',
      opacity: 0,
    },
  },
});

export const title = style({
  fontSize: vars.typography.fontSize.sm,
  fontWeight: vars.typography.fontWeight.bold,
  fontFamily: vars.typography.fontFamily.primary,
  color: vars.typography.textColor.primary,
});

export const body = style({
  fontSize: vars.typography.fontSize.xs,
  fontFamily: vars.typography.fontFamily.primary,
  color: vars.typography.textColor.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // 줄 단위 body(일괄 승인 실패 사유 목록 등)가 첫 줄만 남고 잘리지 않도록 개행을 살린다.
  // 대신 4줄에서 잘라 토스트가 화면을 덮는 것은 막는다 — 기존 한 줄 body 는 그대로 한 줄.
  whiteSpace: 'pre-line',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 4,
});

export const icon = style({
  fontSize: '16px',
  lineHeight: 1,
  flexShrink: 0,
  marginTop: '1px',
});

export const content = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap['2xs'],
  minWidth: 0,
  flex: 1,
});

// 액션 줄 — 본문 아래 왼쪽 정렬. 버튼 표면은 Button link(글자만)라 토스트 글래스 위에 면을 더 얹지 않는다.
export const actionRow = style({
  display: 'flex',
  marginTop: vars.spacing.gap['2xs'],
});

// ─── Type-based accent colors ───

const typeAccents = {
  info: {
    border: vars.opacityScale.primaryBlue.moderate,
    glow: vars.opacityScale.primaryBlue.strong,
    sweep: vars.bright.primary.default,
  },
  success: {
    border: `${vars.opacityScale.successGreen.strong}`,
    glow: vars.opacityScale.successGreen.strong,
    sweep: vars.bright.status.success,
  },
  warning: {
    border: `${vars.opacityScale.warningAmber.firm}`,
    glow: `${vars.opacityScale.warningAmber.moderate}`,
    sweep: vars.bright.status.warning,
  },
  error: {
    border: `${vars.opacityScale.errorRed.firm}`,
    glow: `${vars.opacityScale.errorRed.firm}`,
    sweep: vars.bright.status.error,
  },
} as const;

export const toastType = styleVariants(typeAccents, (accent) => ({
  borderColor: accent.border,
  boxShadow: `${vars.shadow.toast}, 0 0 8px ${accent.glow}`,
}));

// Type-based glow sweep color
Object.entries(typeAccents).forEach(([type, accent]) => {
  globalStyle(`${toast}.${toastType[type as keyof typeof typeAccents]}::after`, {
    background: `linear-gradient(90deg, transparent, ${accent.sweep}, transparent)`,
    backgroundSize: '50% 100%',
    boxShadow: `0 0 6px ${accent.glow}`,
  });
});
